import React, { useState } from 'react'
import FreeSpinPanel from './FreeSpinPanel'

// Footer control buttons. Each PNG is a 2-state sprite (156x81):
// left half = light/normal, right half = dark/pressed (shown on click via :active).
// The hamburger (options) button stays put and toggles the rest.
const CONTROL_BUTTONS = [
    { key: 'menu', img: 'menu 1.png', title: 'Menu' },
    { key: 'linha_menos', img: 'linha_menos.png', title: 'Linha menos' },
    { key: 'linha_mais', img: 'linha_mais.png', title: 'Linha mais' },
    { key: 'aposta_menos', img: 'aposta_menos.png', title: 'Aposta menos' },
    { key: 'aposta_mais', img: 'aposta_mais.png', title: 'Aposta mais' },
    { key: 'ap_max', img: 'ap_max.png', title: 'Aposta máxima' },
    { key: 'auto', img: 'auto.png', title: 'Auto' },
    { key: 'tabela', img: 'tabela.png', title: 'Tabela' },
]

const btnStyle = (img) => ({
    backgroundImage: `url(${process.env.PUBLIC_URL}/ice-assets/line/${encodeURIComponent(img)})`,
})

/* The line beside GIRAR is not a fixed label — the machine changes it, and
   numberCheck.mp4 shows the three states:
     idle      Jogue de 1 a 400 / Créditos
     spinning  BOA SORTE!!!
     won       Ganhou <amount>, then Ganho Total <amount> as it goes to credits

   IT DOES NOT CYCLE WHILE IDLE. A first pass had it alternating BOA SORTE and
   the Jogue line on a 2s/1s beat, which is what the recording looks like until
   you check the reels: differencing the reel window frame to frame puts them
   TURNING at 8.21-10.11s and 11.61-13.31s and STILL at 10.21-11.51s and 13.41s
   on — exactly the stretches showing BOA SORTE and the Jogue line. It was two
   quick spins back to back, not an attract loop. The message just follows the
   reels, so there is no timer here at all.

   `reelsSpinning` is THE REELS, not the request. spinning.mp4 pins how tightly
   the two are tied: over its cleanest spin the reel band's frame-to-frame motion
   runs 16.00-19.00s and BOA SORTE!!! is up for 16.00-19.00s, every frame of it,
   flipping to "Ganhou 200" on the 19.25s frame — the one after the last reel
   stops. So it must not be driven off the API round-trip (jogarButtonFlag), which
   clears the moment the response lands and would drop the line back to "Jogue de
   1 a 400" with all five reels still turning. It also outranks `winnings` here:
   nothing but BOA SORTE shows while anything is rolling.

   The win half needs no clock either. `winnings` counts the prize DOWN into the
   balance (playWinningAnimation, phase 2) while `pago` counts the same money UP,
   and onSpin resets `pago` to null. So `pago == null` with `winnings > 0` means
   the win has landed but the payout has not started — which is exactly the 1.7s
   the recording holds "Ganhou" for. */
/* Free play (freeplaybonus.mp4) changes two of those: while free spins roll the
   line holds "Ganho Total <free play so far>" instead of BOA SORTE!!!, and a win
   reads "Ganhou / 3 x 1250 = 3750" (multiplier x lines = paid). After that the
   line goes back to Ganho Total, which counts up by the win and stays up while
   the win counts into Créditos (showFreePlayWin in HalloweenIceGame). */
function FooterMessage({ reelsSpinning, winnings, pago, freePlayWin, freePlayTotal }) {
    let body
    if (reelsSpinning) body = freePlayTotal != null ? <>Ganho Total<br />{Math.round(freePlayTotal)}</> : <>BOA SORTE!!!</>
    else if (freePlayWin?.stage === 'multiply') body = <>Ganhou<br />{freePlayWin.multiplier} x {Math.round(freePlayWin.base)} = {Math.round(freePlayWin.total)}</>
    else if (winnings > 0 && !freePlayWin) body = <>{pago == null ? 'Ganhou' : 'Ganho Total'}<br />{Math.round(winnings)}</>
    else if (freePlayTotal != null) body = <>Ganho Total<br />{Math.round(freePlayTotal)}</>
    else body = <>Jogue de 1 a 400<br />Créditos</>

    return <div className='ice-credits-text'>{body}</div>
}

// `spinning` disables GIRAR (it tracks the request, so the button comes back as
// soon as the result is in and a click can then stop the reels early);
// `reelsSpinning` is what the message follows. They are not the same window.
// `freeSpins` ({ 1: n, 2: n, 3: n, 5: n }) puts the JOGADA GRÁTIS panel where the
// control buttons are, as the machine does in free play. Pass null for the buttons.
function IceFooter({ onSpin, spinning, locked = false, reelsSpinning, onButtonClick, winnings, pago, freeSpins, freePlayWin, freePlayTotal }) {
    // hamburger toggles the control buttons row (hide / show). Shown by default.
    const [showButtons, setShowButtons] = useState(true)

    return (
        <div className='ice-footer'>
            {/* Two fishing trips alternate on one 24s cycle — first he comes home
                empty, then he lands the big yellow one and carries it off. Two
                elements because each trip runs its own frame sequence off its own
                sheet; the timing lives in ice-man-* in IceGamePlay.css. */}
            <div
                className='ice-man ice-man-nofish'
                style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/ice-assets/line/manFish_strip.png)` }}
                aria-hidden='true'
            />
            <div
                className='ice-man ice-man-catch'
                style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/ice-assets/line/manFishCatch_strip.png)` }}
                aria-hidden='true'
            />
            {/* the two blue marks sitting in the pond — a fixed feature of the
                water, not something swimming, so no animation. See
                .ice-talab-fish in IceGamePlay.css for where they go. */}
            <div
                className='ice-talab-fish'
                style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/ice-assets/line/talabFish.png)` }}
                aria-hidden='true'
            />
            <div
                className="ice-float ice-fundo"
                style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/ice-assets/line/img_iceberg_fundo.png)` }}
                aria-hidden="true"
            />
            {freeSpins ? <FreeSpinPanel counts={freeSpins} /> : <div className='ice-footer-buttons'>
                {/* hamburger: show/hide the rest of the buttons */}
                <button
                    type='button'
                    className='ice-btn'
                    style={btnStyle('spriteButtonOption.png')}
                    title='Opções'
                    aria-label='Opções'
                    onClick={() => setShowButtons((s) => !s)}
                    disabled={locked}
                />

                {showButtons && CONTROL_BUTTONS.map((b) => (
                    <button
                        key={b.key}
                        type='button'
                        className='ice-btn'
                        style={btnStyle(b.img)}
                        title={b.title}
                        aria-label={b.title}
                        onClick={() => onButtonClick && onButtonClick(b.key)}
                        disabled={locked}
                    />
                ))}
            </div>}

            {/* the green one breaking the surface in the hole. The strip is
                spriteFish.png's own art — that file is a packed atlas (the
                poses, the splashes and a ripple scattered around a 64x702
                canvas at arbitrary offsets, some sharing a row), which CSS
                cannot step through, so its pieces are redrawn as an even
                12-frame strip, every frame centred on the same x and sitting on
                the same waterline. Which piece is which frame is written down
                beside .ice-fish in IceGamePlay.css. */}
            <div
                className='ice-fish'
                style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/ice-assets/line/spriteFish_jump.png)` }}
                aria-hidden='true'
            />

            <div
                className='ice-fire'
                style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/ice-assets/line/FishFire.png)` }}
                aria-hidden='true'
            />

            <div className='ice-footer-right'>
                <div className='ice-coin-wrap'>
                    <div
                        className='ice-coin'
                        style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/ice-assets/line/coins.png)` }}
                        aria-hidden='true'
                    />
                    <span className='ice-coin-label'>1 CENTAVO</span>
                </div>

                <FooterMessage reelsSpinning={reelsSpinning} winnings={winnings} pago={pago} freePlayWin={freePlayWin} freePlayTotal={freePlayTotal} />

                <button
                    type='button'
                    className='ice-btn ice-btn-spin'
                    style={btnStyle('spriteButtonSpin.png')}
                    onClick={onSpin}
                    disabled={spinning}
                    title='Girar'
                    aria-label='Girar'
                />
            </div>
        </div>
    )
}

export default IceFooter
